import { useState } from 'react';
import { FileText, Eye, Download, ScrollText } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { Button, Badge, Card, CardBody, CardTitle, CardDescription, Divider, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { AccountBreadcrumbs } from './components/account-breadcrumbs';
import {
  useMyAcceptedRegulations,
  getDownloadUrl,
  type MyAcceptedRegulation,
  type RegulationAttachment,
} from '@/api/account';
import { formatDateTime } from '@/lib/format';
import { InfoGrid } from './components/InfoGrid';

/**
 * Accepted Policies — the regulations the signed-in employee has accepted.
 * Rebuild of datacentral-client-v4 `modules/Account/.../Regulations` composed
 * from @vnetwork/ui primitives (Card / StatusPill / Badge / Divider / Button).
 * PDF attachments are fetched through a presigned URL (never a direct link).
 */
export default function RegulationsPage() {
  const { data, isLoading, isError, refetch } = useMyAcceptedRegulations();
  const rows = data ?? [];

  return (
    <Container size="full">
      <PageHeader breadcrumbs={<AccountBreadcrumbs current="Accepted Policies" />} title="Accepted Policies" description="Regulations and policies you have accepted" />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load accepted policies"
          actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
        />
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i} flat><CardBody><Skeleton style={{ height: 96 }} /></CardBody></Card>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<ScrollText size={24} />} title="No accepted policies" description="You haven't accepted any policies yet." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {rows.map((reg) => <RegulationCard key={reg.acceptance_id} reg={reg} />)}
        </div>
      )}
    </Container>
  );
}

function RegulationCard({ reg }: { reg: MyAcceptedRegulation }) {
  const pdf = reg.attachments?.find((a) => a.attachment_file_type === 'application/pdf');

  return (
    <Card flat>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, display: 'grid', gap: 'var(--vn-space-1)' }}>
            <CardTitle>{reg.regulation_title}</CardTitle>
            {reg.regulation_summary ? (
              <CardDescription>{reg.regulation_summary}</CardDescription>
            ) : null}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexShrink: 0 }}>
            <StatusPill tone={reg.is_active ? 'success' : 'muted'}>{reg.is_active ? 'Active' : 'No longer active'}</StatusPill>
            {reg.is_mandatory ? <Badge tone="warning">Mandatory</Badge> : null}
          </div>
        </div>

        <InfoGrid
          minColWidth={160}
          items={[
            { label: 'Version', value: `v${reg.regulation_version}` },
            { label: 'Type', value: reg.regulation_type },
            { label: 'Accepted', value: formatDateTime(reg.accepted_at) },
            { label: 'Last updated', value: formatDateTime(reg.updated_at) },
          ]}
        />

        {pdf ? (
          <>
            <Divider />
            <AttachmentRow file={pdf} />
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}

function AttachmentRow({ file }: { file: RegulationAttachment }) {
  const [busy, setBusy] = useState<null | 'view' | 'download'>(null);
  const path = file.attachment_file_url;

  const open = async (mode: 'view' | 'download') => {
    if (!path) return;
    setBusy(mode);
    try {
      const url = await getDownloadUrl(path);
      if (!url) return;
      if (mode === 'view') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = file.attachment_file_name || '';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch {
      /* presigned fetch failed — leave the row as-is */
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
      <FileText size={16} color="var(--vn-fg-muted)" style={{ flexShrink: 0 }} />
      <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--vn-font-size-sm)' }}>
        {file.attachment_file_name}
      </span>
      <Button size="sm" variant="ghost" startIcon={<Eye size={16} />} disabled={!path || busy !== null} loading={busy === 'view'} onClick={() => open('view')}>
        View
      </Button>
      <Button size="sm" variant="secondary" startIcon={<Download size={16} />} disabled={!path || busy !== null} loading={busy === 'download'} onClick={() => open('download')}>
        Download
      </Button>
    </div>
  );
}
